import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../services/firebase';
import { getBodyProfile } from '../services/userService';
import Login from './Login';
import BodyTypeSelector from './BodyTypeSelector';

function Navbar({ onUploadClick, onBodyProfileLoad }) {
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showBodyType, setShowBodyType] = useState(false);
  const [bodyProfile, setBodyProfile] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth, async (currentUser) => {
        setUser(currentUser);
        if (currentUser) {
          const profile = await getBodyProfile(currentUser.uid);
          if (profile?.shape) {
            const bp = {
              shape: profile.shape,
              shapeName: profile.shapeName,
              gender: profile.gender,
              size: profile.topSize || profile.size,
              topSize: profile.topSize || profile.size,
              bottomSize: profile.bottomSize || null,
            };
            setBodyProfile(bp);
            if (onBodyProfileLoad) onBodyProfileLoad(bp);
          }
        } else {
          setBodyProfile(null);
          if (onBodyProfileLoad) onBodyProfileLoad(null);
        }
      }
    );
    return () => unsubscribe();
  }, [onBodyProfileLoad]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setBodyProfile(null);
      if (onBodyProfileLoad) onBodyProfileLoad(null);
      setShowMobileMenu(false);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleBodySave = (data) => {
    const bp = {
      shape: data.shape,
      shapeName: data.shapeName,
      gender: data.gender,
      size: data.topSize || data.size,
      topSize: data.topSize || data.size,
      bottomSize: data.bottomSize || null,
    };
    setBodyProfile(bp);
    if (onBodyProfileLoad) onBodyProfileLoad(bp);
    setShowBodyType(false);
  };

  return (
    <>
      <nav style={{
        ...styles.nav,
        padding: isMobile ? '14px 20px' : '18px 40px',
      }}>

        {/* Logo */}
        <div style={styles.logo}>
          Fit<span style={styles.logoAccent}>thea</span>
        </div>

        {/* Desktop Nav Links */}
        {!isMobile && (
          <ul style={styles.navLinks}>
            <li><a href="/" style={styles.link}>Trending</a></li>
            <li><a href="/" style={styles.link}>Occasions</a></li>
            <li><a href="/" style={styles.link}>My Style</a></li>
          </ul>
        )}

        {/* Desktop Right Side */}
        {!isMobile && (
          <div style={styles.navRight}>
            {user ? (
              <div style={styles.userRow}>
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt="profile"
                    style={styles.avatar}
                  />
                ) : (
                  <div style={styles.avatarPlaceholder}>
                    {(user.displayName || user.email || 'U')[0].toUpperCase()}
                  </div>
                )}
                <div style={styles.userInfo}>
                  <span style={styles.userName}>
                    Hi, {user.displayName?.split(' ')[0] ||
                      user.email?.split('@')[0]} 👋
                  </span>
                  {bodyProfile ? (
                    <span style={styles.bodyTag}>
                      {bodyProfile.shapeName}
                      {' · '}Top {bodyProfile.topSize || bodyProfile.size}
                      {bodyProfile.bottomSize && (
                        <> · Bottom {bodyProfile.bottomSize}</>
                      )}
                      <span
                        style={styles.editTag}
                        onClick={() => setShowBodyType(true)}
                      >
                        ✎ Edit
                      </span>
                    </span>
                  ) : (
                    <span
                      style={styles.setProfileTag}
                      onClick={() => setShowBodyType(true)}
                    >
                      + Set body profile
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <button
                style={styles.loginBtn}
                onClick={() => setShowLogin(true)}
              >
                Sign In
              </button>
            )}

            <div style={styles.actionGroup}>
              <div style={styles.actionButtons}>
                <button
                  style={styles.bodyTypeBtn}
                  onClick={() => setShowBodyType(true)}
                >
                  ◎ Body Type
                </button>
                <button
                  style={styles.uploadBtn}
                  onClick={onUploadClick}
                >
                  ✦ AI Body Scan
                </button>
              </div>
              <p style={styles.actionHint}>
                Select manually or let AI detect your body type
              </p>
            </div>

            {user && (
              <button
                style={styles.signOutBtn}
                onClick={handleSignOut}
              >
                Sign Out
              </button>
            )}
          </div>
        )}

        {/* Mobile Right Side */}
        {isMobile && (
          <div style={styles.mobileRight}>
            {user ? (
              <div style={styles.avatarPlaceholder}>
                {(user.displayName || user.email || 'U')[0].toUpperCase()}
              </div>
            ) : (
              <button
                style={styles.mobileSignInBtn}
                onClick={() => setShowLogin(true)}
              >
                Sign In
              </button>
            )}
            <button
              style={styles.bodyTypeBtnMobile}
              onClick={() => setShowBodyType(true)}
            >
              ◎
            </button>
            <button
              style={styles.hamburger}
              onClick={() => setShowMobileMenu(!showMobileMenu)}
            >
              {showMobileMenu ? '✕' : '☰'}
            </button>
          </div>
        )}

      </nav>

      {/* Mobile Menu Dropdown */}
      {isMobile && showMobileMenu && (
        <div style={styles.mobileMenu}>
          {user ? (
            <>
              <div style={styles.mobileUserRow}>
                <div style={styles.avatarPlaceholder}>
                  {(user.displayName || user.email || 'U')[0].toUpperCase()}
                </div>
                <div>
                  <div style={styles.mobileUserName}>
                    Hi, {user.displayName?.split(' ')[0] ||
                      user.email?.split('@')[0]} 👋
                  </div>
                  {bodyProfile && (
                    <div style={styles.mobileBodyTag}>
                      {bodyProfile.shapeName}
                      {' · '}Top {bodyProfile.topSize || bodyProfile.size}
                      {bodyProfile.bottomSize && (
                        <> · Bottom {bodyProfile.bottomSize}</>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <button
                style={styles.mobileMenuBtn}
                onClick={() => {
                  setShowBodyType(true);
                  setShowMobileMenu(false);
                }}
              >
                ◎ Edit Body Profile
              </button>

              <button
                style={styles.mobileMenuBtn}
                onClick={() => {
                  onUploadClick();
                  setShowMobileMenu(false);
                }}
              >
                ✦ AI Body Scan
              </button>

              <button
                style={styles.mobileSignOutBtn}
                onClick={handleSignOut}
              >
                Sign Out
              </button>
            </>
          ) : (
            <>
              <button
                style={styles.mobileLoginBtn}
                onClick={() => {
                  setShowLogin(true);
                  setShowMobileMenu(false);
                }}
              >
                Sign In to Fitthea
              </button>
              <button
                style={styles.mobileMenuBtn}
                onClick={() => {
                  setShowBodyType(true);
                  setShowMobileMenu(false);
                }}
              >
                ◎ Select Body Type
              </button>
              <button
                style={styles.mobileMenuBtn}
                onClick={() => {
                  onUploadClick();
                  setShowMobileMenu(false);
                }}
              >
                ✦ AI Body Scan
              </button>
            </>
          )}
        </div>
      )}

      {/* Login Modal */}
      {showLogin && (
        <Login onClose={() => setShowLogin(false)} />
      )}

      {/* Body Type Selector Modal */}
      {showBodyType && (
        <BodyTypeSelector
          user={user}
          onClose={() => setShowBodyType(false)}
          onSave={handleBodySave}
          existingProfile={bodyProfile}
        />
      )}
    </>
  );
}

const styles = {
  nav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#FFF9F4',
    borderBottom: '1px solid #EDE4DA',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  logo: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '22px',
    fontWeight: '700',
    color: '#2C2420',
    letterSpacing: '-0.5px',
    flexShrink: 0,
  },
  logoAccent: {
    color: '#C8715A',
    fontStyle: 'italic',
  },
  navLinks: {
    display: 'flex',
    gap: '32px',
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  link: {
    color: '#8A7F7A',
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: '500',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  navRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexShrink: 0,
  },
  userRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #EDE4DA',
  },
  avatarPlaceholder: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: '#C8715A',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '600',
    flexShrink: 0,
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  userName: {
    fontSize: '13px',
    color: '#2C2420',
    fontWeight: '500',
    whiteSpace: 'nowrap',
  },
  bodyTag: {
    fontSize: '11px',
    color: '#C8715A',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexWrap: 'wrap',
  },
  editTag: {
    fontSize: '10px',
    color: '#8A7F7A',
    cursor: 'pointer',
    textDecoration: 'underline',
    marginLeft: '4px',
  },
  setProfileTag: {
    fontSize: '11px',
    color: '#C8715A',
    cursor: 'pointer',
    fontWeight: '500',
    textDecoration: 'underline',
  },
  loginBtn: {
    background: 'none',
    border: '1.5px solid #2C2420',
    borderRadius: '100px',
    padding: '8px 18px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#2C2420',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    whiteSpace: 'nowrap',
  },
  actionGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  bodyTypeBtn: {
    background: 'none',
    border: '1.5px solid #C8715A',
    borderRadius: '100px',
    padding: '8px 16px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#C8715A',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    whiteSpace: 'nowrap',
  },
  uploadBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#2C2420',
    color: 'white',
    border: 'none',
    padding: '10px 16px',
    borderRadius: '100px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    whiteSpace: 'nowrap',
  },
  actionHint: {
    fontSize: '10px',
    color: '#8A7F7A',
    margin: 0,
    whiteSpace: 'nowrap',
    letterSpacing: '0.2px',
  },
  signOutBtn: {
    background: 'none',
    border: '1px solid #EDE4DA',
    borderRadius: '100px',
    padding: '7px 14px',
    fontSize: '12px',
    color: '#8A7F7A',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    whiteSpace: 'nowrap',
  },
  mobileRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  mobileSignInBtn: {
    background: 'none',
    border: '1.5px solid #2C2420',
    borderRadius: '100px',
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#2C2420',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    whiteSpace: 'nowrap',
  },
  bodyTypeBtnMobile: {
    background: 'none',
    border: '1.5px solid #C8715A',
    borderRadius: '100px',
    padding: '7px 12px',
    fontSize: '14px',
    color: '#C8715A',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  hamburger: {
    background: 'none',
    border: 'none',
    fontSize: '22px',
    cursor: 'pointer',
    color: '#2C2420',
    padding: '4px 8px',
    lineHeight: 1,
  },
  mobileMenu: {
    background: 'white',
    borderBottom: '1px solid #EDE4DA',
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    position: 'sticky',
    top: '57px',
    zIndex: 99,
    boxShadow: '0 4px 12px rgba(44,36,32,0.08)',
  },
  mobileUserRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0 12px',
    borderBottom: '1px solid #EDE4DA',
    marginBottom: '4px',
  },
  mobileUserName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#2C2420',
    marginBottom: '2px',
  },
  mobileBodyTag: {
    fontSize: '11px',
    color: '#C8715A',
    fontWeight: '500',
  },
  mobileMenuBtn: {
    background: '#F5F5F5',
    border: 'none',
    borderRadius: '10px',
    padding: '13px 16px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#2C2420',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    textAlign: 'left',
    width: '100%',
  },
  mobileLoginBtn: {
    background: '#2C2420',
    border: 'none',
    borderRadius: '10px',
    padding: '13px 16px',
    fontSize: '13px',
    fontWeight: '600',
    color: 'white',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    width: '100%',
    textAlign: 'center',
  },
  mobileSignOutBtn: {
    background: 'none',
    border: '1px solid #EDE4DA',
    borderRadius: '10px',
    padding: '13px 16px',
    fontSize: '13px',
    color: '#8A7F7A',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    width: '100%',
    textAlign: 'left',
    marginTop: '4px',
  },
};

export default Navbar;